import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Mail, MapPin } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { DEMO_EMAIL, DEMO_MAILTO } from "@/lib/scroll";

const CTA_IMG =
    "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80";

export const Contact = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

    return (
        <section
            ref={ref}
            id="contact"
            className="relative bg-[#091524] text-white overflow-hidden"
        >
            {/* Parallax image band */}
            <motion.img
                src={CTA_IMG}
                alt="Industrial operations at dusk"
                style={{ y: imgY }}
                className="absolute inset-0 w-full h-[124%] object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-[#091524]/70" />
            <div
                className="absolute inset-0 opacity-20 pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
                    backgroundSize: "64px 64px",
                }}
            />

            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-36">
                <div className="max-w-4xl">
                    <span className="label-eyebrow text-[#0052CC]">// Request a Demo</span>
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5rem] font-extrabold tracking-tight leading-[0.95]"
                    >
                        See Apex Pro
                        <br />
                        <span className="text-[#0052CC]">in action.</span>
                    </motion.h2>
                    <p className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
                        Book a personalised walkthrough with our team. We&apos;ll show you exactly
                        how Apex Pro maps to your operations &mdash; no generic sales deck. Get in
                        touch and we&apos;ll respond within one business day.
                    </p>

                    <div className="mt-12 flex flex-col sm:flex-row gap-4">
                        <a
                            data-testid={LANDING.contactSubmit}
                            href={DEMO_MAILTO}
                            className="group inline-flex items-center justify-center gap-2 bg-[#0052CC] hover:bg-[#0047B3] text-white px-9 py-5 rounded-sm font-semibold text-lg transition-all duration-300 hover:-translate-y-0.5"
                        >
                            Request a Demo
                            <ArrowUpRight
                                size={20}
                                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                            />
                        </a>
                        <a
                            data-testid={LANDING.contactEmailLink}
                            href={`mailto:${DEMO_EMAIL}`}
                            className="inline-flex items-center justify-center gap-2 border-2 border-white/25 hover:border-white text-white hover:bg-white hover:text-[#091524] px-9 py-5 rounded-sm font-semibold text-lg transition-all duration-300"
                        >
                            {DEMO_EMAIL}
                        </a>
                    </div>

                    {/* Contact details */}
                    <div className="mt-16 pt-10 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl">
                        <div className="flex items-start gap-4">
                            <span className="w-10 h-10 shrink-0 bg-white/10 flex items-center justify-center rounded-sm">
                                <Mail size={16} className="text-[#0052CC]" />
                            </span>
                            <div>
                                <div className="label-eyebrow text-white/40">Email</div>
                                <a
                                    href={`mailto:${DEMO_EMAIL}`}
                                    className="text-white font-medium hover:text-[#0052CC] transition"
                                >
                                    {DEMO_EMAIL}
                                </a>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <span className="w-10 h-10 shrink-0 bg-white/10 flex items-center justify-center rounded-sm">
                                <MapPin size={16} className="text-[#0052CC]" />
                            </span>
                            <div>
                                <div className="label-eyebrow text-white/40">Office</div>
                                <address className="not-italic text-white/85 font-medium text-sm leading-relaxed">
                                    Office No: CWS-2V-227744, 26th Floor<br />
                                    Amber Gem Tower, Sheikh Khalifa Street<br />
                                    Ajman, United Arab Emirates<br />
                                    P.O. Box 4848
                                </address>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
